import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ConsentDialog from '@/components/ConsentDialog';
import RoleCaptureModal from '@/components/RoleCaptureModal';
import { TOP_BAR_STICKY } from '@/components/StickyBar';
import {
  LayoutDashboard,
  FilePlus2,
  Menu,
  X,
  Settings,
  LogOut,
  User,
  Info,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const {
    user, logout, usageLimit, isUnlimited, consentGiven, giveConsent,
    userRole, betaProcurementCount, daysRemaining, procurementsRemaining,
    checkAuth
  } = useAuth();
  
  const navItems = [
    { path: '/about', label: 'About ProcMatrix', icon: Info },
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/new', label: 'New Procurement', icon: FilePlus2 },
    { path: '/admin/master-data', label: 'Master Data', icon: Settings },
  ];
  
  const isActive = (path) => location.pathname === path;

  // Item 3 (2026-07-22 pre-merge): captured persona shown in the identity
  // area - 'Other' prefers the buyer's own free text when they gave one.
  const displayRole = user?.role
    ? (user.role === 'Other' ? (user.role_other_text?.trim() || 'Other') : user.role)
    : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Consent Dialog - Shows if user hasn't given consent */}
      {user && !consentGiven && (
        <ConsentDialog
          open={true}
          onConsent={giveConsent}
          onDecline={logout}
        />
      )}

      {/* First-login role capture (Item 5, 2026-07-21) - after consent, so
          the two non-dismissable modals never race/stack. Existing users
          with no role get this too, on their next login. */}
      {user && consentGiven && !user.role && (
        <RoleCaptureModal open={true} onCaptured={checkAuth} />
      )}

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-20 bg-white/90 backdrop-blur-xl border-b border-zinc-200 z-50 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="/sahinta-logo.png" 
            alt="Sahinta - The Wisdom" 
            className="h-14 w-auto"
          />
        </div>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-zinc-50 rounded-sm"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen w-64 bg-zinc-50 border-r border-zinc-200 z-40
        transform transition-transform duration-200 ease-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="px-3 py-4 flex items-center justify-center border-b border-zinc-700 bg-[#0a1628]">
          <Link to="/" className="block w-full">
            <img 
              src="/sahinta-logo.png" 
              alt="Sahinta - The Wisdom" 
              className="w-full h-auto max-h-20 object-contain"
            />
          </Link>
        </div>
        
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium
                transition-all duration-200
                ${isActive(item.path) 
                  ? 'bg-[#0055FF] text-white' 
                  : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }
              `}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label}
            </Link>
          ))}
        </nav>
        
        {/* User Profile & Logout */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-zinc-200">
          {user && (
            <div className="p-4 border-b border-zinc-200">
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#0055FF] flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">
                    {user.email}
                  </p>
                  {/* Captured persona (Item 3, 2026-07-22 pre-merge) - 'Other'
                      shows its free text if given, else the literal label.
                      No role captured yet renders nothing here. */}
                  {displayRole && (
                    <p className="text-xs text-zinc-400 truncate" data-testid="user-role-label">
                      {displayRole}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Usage Credits Display */}
              <div className="mt-3 p-2 bg-zinc-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-medium text-zinc-700">Credits</span>
                  </div>
                  <span className={`text-xs font-bold ${isUnlimited ? 'text-emerald-600' : usageLimit > 3 ? 'text-emerald-600' : usageLimit > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                    {isUnlimited ? 'Unlimited' : usageLimit !== null ? usageLimit : '...'}
                  </span>
                </div>
                {!isUnlimited && usageLimit !== null && usageLimit <= 3 && usageLimit > 0 && (
                  <p className="text-[10px] text-amber-600 mt-1">Running low on credits</p>
                )}
                {!isUnlimited && usageLimit === 0 && (
                  <a
                    href="mailto:ashokr@sahinta.com?subject=ProcMatrix Beta - Credit Request"
                    className="text-[10px] text-red-600 mt-1 block hover:underline"
                  >
                    Email ashokr@sahinta.com for more
                  </a>
                )}
              </div>

              {userRole === 'beta' && (
                <div className="mt-3 p-2 bg-zinc-50 rounded-lg">
                  <span className="text-xs font-medium text-zinc-700">
                    Beta: {betaProcurementCount ?? 0}/10 procurements | {daysRemaining ?? '--'} day{daysRemaining === 1 ? '' : 's'} left
                  </span>
                  {procurementsRemaining === 1 && (
                    <p className="text-[10px] text-amber-600 mt-1">
                      This is your last beta procurement. Contact{' '}
                      <a href="mailto:ashokr@sahinta.com" className="underline">ashokr@sahinta.com</a> for more.
                    </p>
                  )}
                  {daysRemaining !== null && daysRemaining <= 2 && (
                    <p className="text-[10px] text-red-600 mt-1">
                      Beta access expires in {daysRemaining} day{daysRemaining === 1 ? '' : 's'}. Contact{' '}
                      <a href="mailto:ashokr@sahinta.com" className="underline">ashokr@sahinta.com</a> to extend.
                    </p>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="w-full mt-3 text-zinc-600 hover:text-red-600 hover:border-red-200"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          )}
          
          <div className="p-4">
            <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mb-2">
              System Status
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-zinc-600">All agents operational</span>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Main Content */}
      <main className="lg:pl-64 min-h-screen">
        {/* Top bar with ProcMatrix branding - desktop only, sticky (occupies flow space) */}
        <div className={`hidden lg:flex h-14 items-center justify-end px-6 ${TOP_BAR_STICKY}`}>
          <img src="/brand/logo-wordmark.svg" alt="ProcMatrix" className="h-8 w-auto" />
        </div>
        <div className="pt-20 lg:pt-0">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
